
import { CHROME_ASSETS, elem, installChrome, styleInstaller } from "./chrome";

export interface SystemMenuActions {
  /** Open the System Options panel (`uiSystemOption.OptionDialog`). */
  readonly onSystemOptions: () => void;
  /** Open the Game Options panel (`uiGameOption.OptionDialog`). */
  readonly onGameOptions: () => void;
  /** Return to the existing login/character-select flow, when available. */
  readonly onCharacterSelect?: () => void;
  /** End this session, when the application has a session to close. */
  readonly onLogout?: () => void;
  readonly onExit?: () => void;
  /** Open the existing local help window. */
  readonly onHelp?: () => void;
  /** Open the item-shop / mall UI, when the shell wires one. */
  readonly onMall?: () => void;
}

/** `locale_interface.txt` strings used by `systemdialog.py`. */
const LABELS = {
  help: "Help",
  mall: "Item Shop",
  systemOptions: "System Options",
  gameOptions: "Game Options",
  change: "Change Character",
  logout: "Log Out",
  exit: "Exit Game",
  cancel: "Cancel",
} as const;

const STYLE_ID = "system-menu-style";
const STYLE = `
.system-dialog {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 288px;
  z-index: 80;
  pointer-events: auto;
  box-sizing: border-box;
}
.system-dialog .m2-xlarge {
  position: absolute;
  left: 10px;
  width: 180px;
}
.system-dialog .system-mall {
  color: #F8BF24;
  background-image: url("${CHROME_ASSETS.xlargeOver}");
}
.system-dialog .system-mall:hover,
.system-dialog .system-mall:active {
  background-image: url("${CHROME_ASSETS.xlargeOver}");
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

type ButtonSpec = {
  readonly action: string;
  readonly label: string;
  readonly y: number;
  readonly mall?: boolean;
};

/** Visible system-menu rows: `locale/en/ui/systemdialog.py`'s own eight,
 *  in its own order. */
export const BUTTONS: readonly ButtonSpec[] = [
  { action: "help", label: LABELS.help, y: 17 },
  { action: "mall", label: LABELS.mall, y: 57, mall: true },
  { action: "system-options", label: LABELS.systemOptions, y: 87 },
  { action: "game-options", label: LABELS.gameOptions, y: 117 },
  { action: "character-select", label: LABELS.change, y: 147 },
  { action: "logout", label: LABELS.logout, y: 177 },
  { action: "exit", label: LABELS.exit, y: 217 },
  { action: "cancel", label: LABELS.cancel, y: 247 },
];

export class SystemMenu {
  private readonly root: HTMLElement;

  constructor(
    mount: HTMLElement,
    private readonly actions: SystemMenuActions,
  ) {
    const doc = mount.ownerDocument;
    installChrome(doc);
    installStyle(doc);

    this.root = elem(doc, "section", "system-dialog m2-thinboard");
    this.root.setAttribute("aria-label", "System menu");
    this.root.setAttribute("role", "dialog");
    this.root.hidden = true;

    for (const spec of BUTTONS) {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = spec.mall ? "m2-xlarge system-action system-mall" : "m2-xlarge system-action";
      button.dataset.action = spec.action;
      button.textContent = spec.label;
      button.style.top = `${spec.y}px`;
      this.root.append(button);
    }

    this.bind("help", () => this.actions.onHelp?.());
    this.bind("mall", () => this.actions.onMall?.());
    this.bind("system-options", () => this.actions.onSystemOptions());
    this.bind("game-options", () => this.actions.onGameOptions());
    this.bind("character-select", () => this.actions.onCharacterSelect?.());
    this.bind("logout", () => this.actions.onLogout?.());
    this.bind("exit", () => this.actions.onExit?.());
    this.root.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener("click", () => {
      this.setVisible(false);
    });

    // `SystemDialog.OnPressEscapeKey` closes the menu and consumes Escape
    // (`uisystem.py:154-156`). Listen on the document so a visible dialog
    // owns the key without needing DOM focus first.
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.setVisible(false);
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });

    mount.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  toggle(): void {
    this.setVisible(!this.showing);
  }

  private bind(action: string, run: () => void): void {
    this.root.querySelector<HTMLButtonElement>(`[data-action="${action}"]`)?.addEventListener("click", () => {
      this.choose(run);
    });
  }

  private choose(action: () => void): void {
    this.setVisible(false);
    action();
  }
}
